import { useId, useRef, type PointerEventHandler, type SyntheticEvent } from "react";
import { canvasCopy } from "../copy/zh-CN.js";
import type { CanvasGroupBounds } from "./group-overlay.js";

interface CanvasGroupOverlayProps {
  group: CanvasGroupBounds;
  zoom: number;
  selected: boolean;
  onSelect: (groupId: string) => void;
  onUngroup: (groupId: string) => void;
  onDragStart: (event: Parameters<PointerEventHandler<HTMLButtonElement>>[0], groupId: string) => void;
  onDragMove: PointerEventHandler<HTMLButtonElement>;
  onDragEnd: PointerEventHandler<HTMLButtonElement>;
  onDragCancel: PointerEventHandler<HTMLButtonElement>;
}

function preventCanvasImport(event: SyntheticEvent) {
  event.preventDefault();
  event.stopPropagation();
}

export function CanvasGroupOverlay({ group, zoom, selected, onSelect, onUngroup,
  onDragStart, onDragMove, onDragEnd, onDragCancel }: CanvasGroupOverlayProps) {
  const nameHeadingId = useId();
  const nameDialogRef = useRef<HTMLDialogElement>(null);
  const nameButtonRef = useRef<HTMLButtonElement>(null);
  return <div className="studio-group-overlay" style={{ left: group.x, top: group.y,
    width: group.width, height: group.height, borderColor: group.color }}>
    <div className="studio-group-overlay__header" style={{ transform: `scale(${1 / zoom}) translateY(-100%)` }}>
      <button className="studio-group-overlay__move" type="button"
        aria-label={`${canvasCopy.moveGroup} ${group.label}`} aria-pressed={selected} title={group.label}
        onClick={() => onSelect(group.id)} onPointerDown={event => onDragStart(event, group.id)}
        onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragCancel}>
        <span>{group.label}</span>
      </button>
      {selected ? <div className="studio-group-overlay__actions" role="group" aria-label={canvasCopy.groupActions}>
        <button ref={nameButtonRef} className="studio-group-overlay__name" type="button"
          aria-label={canvasCopy.viewGroupName} aria-haspopup="dialog"
          onClick={() => nameDialogRef.current?.showModal()}>{canvasCopy.fullName}</button>
        <button className="studio-group-overlay__ungroup" type="button"
          aria-label={`${canvasCopy.ungroup} ${group.label}`}
          onClick={() => onUngroup(group.id)}>{canvasCopy.ungroup}</button>
      </div> : null}
    </div>
    {/* Native top-layer modal escapes canvas clipping/zoom and supplies focus
        containment and Escape dismissal without global keyboard listeners. */}
    <dialog ref={nameDialogRef} className="studio-group-name-dialog" aria-labelledby={nameHeadingId}
      onKeyDown={event => event.stopPropagation()} onKeyUp={event => event.stopPropagation()}
      onPaste={preventCanvasImport} onDragEnter={preventCanvasImport}
      onDragOver={preventCanvasImport} onDrop={preventCanvasImport}
      onClose={() => nameButtonRef.current?.focus({ preventScroll: true })}>
      <h2 id={nameHeadingId}>{canvasCopy.groupFullName}</h2>
      <p>{group.label}</p>
      <button type="button" autoFocus onClick={() => nameDialogRef.current?.close()}>{canvasCopy.closeGroupName}</button>
    </dialog>
  </div>;
}
